"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withBackend = exports.useBackendStrict = exports.useBackend = exports.BackendProvider = void 0;
var tslib_1 = require("tslib");
// (C) 2019 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var wrapDisplayName_1 = require("./wrapDisplayName");
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var BackendContext = react_1.default.createContext(undefined);
BackendContext.displayName = "BackendContext";
/**
 * BackendProvider can be used to inject analytical backend instance to all ui-sdk components in your app.
 *
 * @public
 */
exports.BackendProvider = function (_a) {
    var children = _a.children, backend = _a.backend;
    return react_1.default.createElement(BackendContext.Provider, { value: backend }, children);
};
/**
 * Hook to get analytical backend instance provided to {@link BackendProvider}.
 * You can optionally set a backend override that will be returned if defined.
 * This makes the usage more ergonomic (see the following example).
 *
 * Note: For a better TypeScript experience without the hassle of undefined values, you can use the {@link useBackendStrict} hook.
 *
 * @example
 * ```
 * // instead of
 * const fromContext = useBackend();
 * const effectiveBackend = fromArguments ?? fromContext.
 * // you can write
 * const backend = useBackend(fromArguments);
 *```
 *
 * @param backend - backend to use instead of context value. If undefined, the context value is used.
 * @public
 */
exports.useBackend = function (backend) {
    var backendFromContext = react_1.default.useContext(BackendContext);
    return backend !== null && backend !== void 0 ? backend : backendFromContext;
};
/**
 * Hook to get analytical backend instance provided to {@link BackendProvider}.
 * You can optionally set a backend override that will be returned if defined.
 * This makes the usage more ergonomic (see the following example).
 *
 * Note: If you do not provide an {@link @gooddata/sdk-backend-spi#IAnalyticalBackend} instance to {@link BackendProvider} or as a parameter for this hook,
 * an invariant error is raised.
 *
 * @example
 * ```
 * // instead of
 * const fromContext = useBackendStrict();
 * const effectiveBackend = fromArguments ?? fromContext.
 * // you can write
 * const backend = useBackendStrict(fromArguments);
 *```
 *
 * @param backend - backend to use instead of context value. If undefined, the context value is used.
 * @param context - optionally provide context to improve error message in raised invariant (e.g. parent hook name).
 * @public
 */
exports.useBackendStrict = function (backend, context) {
    if (context === void 0) { context = "useBackendStrict"; }
    var backendFromContext = react_1.default.useContext(BackendContext);
    var effectiveBackend = backend !== null && backend !== void 0 ? backend : backendFromContext;
    ts_invariant_1.default(effectiveBackend, context + ": IAnalyticalBackend must be defined. Either pass it as a parameter or make sure there is a BackendProvider up the component tree.");
    return effectiveBackend;
};
/**
 * Wraps component into a BackendContext consumer - injecting an instance of backend from context into the
 * backend prop.
 *
 * @internal
 */
function withBackend(Component) {
    var ComponentWithInjectedBackend = function (props) {
        return (react_1.default.createElement(BackendContext.Consumer, null, function (backend) { var _a; return react_1.default.createElement(Component, tslib_1.__assign({}, props, { backend: (_a = props.backend) !== null && _a !== void 0 ? _a : backend })); }));
    };
    return wrapDisplayName_1.wrapDisplayName("withBackend", Component)(ComponentWithInjectedBackend);
}
exports.withBackend = withBackend;
//# sourceMappingURL=BackendContext.js.map